import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // A panel pinned to the viewport is already where it belongs: nudging it for
    // an overflow it cannot have would push it off-screen. Any offset left from a
    // previous layout is cleared.
    if (getComputedStyle(this).position === 'fixed') {
      this.style.left = '';
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Vertical placement is left to CSS (`top: 100%`), which tracks the height of
    // the summary above it. Setting it here would pin the panel to a fixed offset
    // and overlap a summary that grows when opened.

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  /**
   * Collapses the details that owns this panel and returns focus to its summary.
   *
   * Lives here rather than on `accordion-custom` because the `on:` attributes
   * only dispatch to `Component` subclasses, and that element extends
   * HTMLElement — pointing at it fails silently.
   */
  close() {
    const details = this.closest('details');

    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = false;
    details.querySelector('summary')?.focus();
  }

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
